/**
 * CareCertificateInductionPanel
 *
 * Worker-facing Care Certificate induction dashboard.
 *
 * Shows the Care Certificate standards and local induction gates grouped by completion type:
 *   - automatic  → evidence-driven, no worker action
 *   - hybrid     → worker fills a short form, admin signs off
 *   - manual     → shadow shift, awaiting manager action
 *
 * Hybrid items open an inline form modal; workers can save drafts or submit.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { GraduationCap, Sparkles, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from './WorkerPremiumCard';
import CareCertificateTrainingCard from '../compliance/CareCertificateTrainingCard';
import TrainingAuditHub from '../training/TrainingAuditHub';

const API = API_BASE;

// ─── Status badge ──────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  completed:          { label: 'Completed',           color: 'bg-green-100 text-green-800' },
  signed_off:         { label: 'Completed',           color: 'bg-green-100 text-green-800' },
  not_started:        { label: 'Not started',         color: 'bg-slate-100 text-slate-600' },
  awaiting_manager_signoff: { label: 'Awaiting manager sign-off', color: 'bg-amber-100 text-amber-800' },
  awaiting_signoff:   { label: 'Awaiting manager',    color: 'bg-amber-100 text-amber-800' },
  submitted:          { label: 'Awaiting manager',    color: 'bg-amber-100 text-amber-800' },
  returned:           { label: 'Returned – revise',   color: 'bg-red-100 text-red-800' },
  draft:              { label: 'Draft saved',          color: 'bg-[#EEF3FD] text-[#2F6FED]' },
  pending:            { label: 'Not started',          color: 'bg-slate-100 text-slate-600' },
  pending_evidence:   { label: 'Training evidence needed', color: 'bg-amber-100 text-amber-800' },
  training_required:  { label: 'Training required',    color: 'bg-amber-100 text-amber-800' },
  awaiting_manager:   { label: 'Awaiting manager',    color: 'bg-amber-100 text-amber-800' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function getHybridDisplayStatus(item) {
  const subStatus = item?.submission_status;
  if (subStatus === 'returned') return 'returned';
  if (subStatus === 'submitted') return 'awaiting_manager_signoff';
  if (subStatus === 'signed_off') {
    return item?.status === 'completed' ? 'completed' : (item?.status || 'pending_evidence');
  }
  if (subStatus === 'draft') return 'draft';
  return 'not_started';
}

function itemNeedsWorkerAction(item) {
  if (['completed', 'signed_off'].includes(item?.status)) return false;
  if (item?.completion_type === 'hybrid') {
    return ['training_required', 'start_form', 'complete_form', 'resubmit'].includes(item.worker_action);
  }
  if (item?.completion_type === 'automatic') return false;
  if (item?.completion_type === 'manual') return false;
  return !['completed', 'signed_off', 'pending_evidence', 'awaiting_manager'].includes(item?.status);
}

function renderHybridActionButton(item, onOpen, { size = 'sm' } = {}) {
  const wa = item.worker_action;
  const sizeClass = size === 'xs'
    ? 'px-3 py-1 rounded-md text-xs font-medium'
    : size === 'md'
      ? 'shrink-0 rounded-xl px-4 py-2 text-sm font-medium'
      : 'text-sm px-3 py-1.5 rounded-xl font-medium';

  if (wa === 'training_required') {
    return (
      <span className={`${sizeClass} inline-flex items-center text-teal-800 bg-teal-50`}
        data-testid="induction-training-managed-by-employer">
        Your team will arrange training
      </span>
    );
  }
  if (wa === 'awaiting_review' || item.submission_status === 'submitted') {
    return (
      <button
        type="button"
        onClick={onOpen}
        className={`${sizeClass} text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200`}
      >
        View submission
      </button>
    );
  }
  if (wa === 'resubmit') {
    return (
      <button type="button" onClick={onOpen} className={`${sizeClass} text-white bg-red-600 hover:bg-red-700`}>
        Revise &amp; resubmit
      </button>
    );
  }
  if (wa === 'complete_form') {
    return (
      <button type="button" onClick={onOpen} className={`${sizeClass} text-white bg-[#0F5C5E] hover:bg-[#0c4a4c]`}>
        Continue
      </button>
    );
  }
  if (wa === 'start_form') {
    return (
      <button type="button" onClick={onOpen} className={`${sizeClass} text-white bg-[#0F5C5E] hover:bg-[#0c4a4c]`}>
        {size === 'xs' ? 'Start form' : 'Start'}
      </button>
    );
  }
  return null;
}

function itemLabel(item) {
  return item?.title || item?.name || item?.code || 'Care Certificate standard';
}

// ─── Form field renderers ──────────────────────────────────────────────────

function FormField({ field, value, onChange, disabled }) {
  const baseClass =
    'mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm ' +
    'focus:outline-none focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E] ' +
    (disabled ? 'bg-slate-50 cursor-not-allowed opacity-70' : '');

  // Soft word-count feedback for text/textarea fields
  const wordCount = (typeof value === 'string') ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  const MIN_WORDS = 20;
  const isTooShort = !disabled && typeof value === 'string' && value.trim().length > 0 && wordCount < MIN_WORDS;

  if (field.type === 'textarea') {
    return (
      <div>
        <textarea
          className={baseClass}
          rows={4}
          maxLength={5000}
          disabled={disabled}
          placeholder={field.hint || ''}
          value={value || ''}
          onChange={e => onChange(field.key, e.target.value)}
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-slate-400">Recommended: 2–4 sentences</span>
          {isTooShort && (
            <span className="text-xs text-amber-600">Try to add a little more detail</span>
          )}
        </div>
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div>
        <input
          type="text"
          className={baseClass}
          maxLength={2000}
          disabled={disabled}
          placeholder={field.hint || ''}
          value={value || ''}
          onChange={e => onChange(field.key, e.target.value)}
        />
        {isTooShort && (
          <span className="text-xs text-amber-600 mt-1 block">Try to add a little more detail</span>
        )}
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <div className="mt-1 space-y-2">
        {(field.options || []).map(opt => (
          <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={field.key}
              value={opt.value}
              disabled={disabled}
              checked={value === opt.value}
              onChange={() => onChange(field.key, opt.value)}
              className="mt-0.5 text-[#0F5C5E]"
            />
            <span className="text-sm text-slate-700">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'checkboxes') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="mt-1 space-y-2">
        {(field.options || []).map(opt => (
          <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              value={opt.value}
              disabled={disabled}
              checked={selected.includes(opt.value)}
              onChange={e => {
                const next = e.target.checked
                  ? [...selected, opt.value]
                  : selected.filter(v => v !== opt.value);
                onChange(field.key, next);
              }}
              className="mt-0.5 text-[#0F5C5E] rounded"
            />
            <span className="text-sm text-slate-700">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  return null;
}

// ─── Hybrid form modal ─────────────────────────────────────────────────────

function HybridFormModal({ formEntry, onClose, onSaved, onSubmitted }) {
  const [schema, setSchema] = useState(null);
  const [learningContent, setLearningContent] = useState(null);
  const [trainingResources, setTrainingResources] = useState([]);
  const [programmeResources, setProgrammeResources] = useState(null);
  const [step, setStep] = useState('learning');
  const [formData, setFormData] = useState({});
  const [subStatus, setSubStatus] = useState(null);
  const [returnReason, setReturnReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formId = formEntry.form_id;
  const isReadOnly = subStatus === 'submitted' || subStatus === 'signed_off';

  useEffect(() => {
    const token = localStorage.getItem('workerToken');
    axios.get(`${API}/worker/induction/forms/${formId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setSchema(res.data.schema);
        setLearningContent(res.data.learning_content || null);
        setTrainingResources(res.data.training_resources || formEntry.training_resources || []);
        setProgrammeResources(res.data.programme_resources || null);
        setSubStatus(res.data.submission_status);
        setReturnReason(res.data.return_reason);
        const prefill = res.data.draft_data || res.data.submitted_data || {};
        if (res.data.submission_status === 'submitted' || res.data.submission_status === 'signed_off') {
          setStep('form');
        }
        setFormData(prefill);
      })
      .catch(() => toast.error('Could not load form.'))
      .finally(() => setLoading(false));
  }, [formId]);

  const handleChange = useCallback((key, val) => {
    setFormData(prev => ({ ...prev, [key]: val }));
  }, []);

  const hasLearningContent = learningContent && (
    learningContent.overview ||
    learningContent.expected_to_know?.length ||
    learningContent.guidance?.length ||
    learningContent.dos?.length ||
    learningContent.donts?.length
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(`${API}/worker/induction/forms/${formId}/save`, {
        data: formData,
        is_draft: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Draft saved.');
      setSubStatus('draft');
      onSaved && onSaved(formId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      await axios.post(`${API}/worker/induction/forms/${formId}/submit`, {
        data: formData,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Form submitted for manager review.');
      setSubStatus('submitted');
      onSubmitted && onSubmitted(formId);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.errors) {
        detail.errors.forEach(e => toast.error(e));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to submit form.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] flex items-start justify-center bg-black/40 overflow-y-auto py-10 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">
              Standard {formEntry.standard_number}
            </p>
            <h2 className="text-base font-semibold text-slate-900">{formEntry.title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {loading && <p className="text-sm text-slate-400">Loading form…</p>}

          {!loading && returnReason && subStatus === 'returned' && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm font-semibold text-red-700 mb-1">Returned for correction</p>
              <p className="text-sm text-red-600">{returnReason}</p>
            </div>
          )}

          {!loading && subStatus === 'submitted' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-700">This form is awaiting manager review. You cannot edit it right now.</p>
            </div>
          )}

          {!loading && subStatus === 'signed_off' && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3">
              <p className="text-sm text-green-700 font-medium">This standard has been signed off by your manager.</p>
            </div>
          )}

          {!loading && step === 'learning' && (
            <div className="space-y-4">
              <CareCertificateTrainingCard
                programme={programmeResources}
                resources={trainingResources}
                standardNumber={formEntry.standard_number}
                standardTitle={formEntry.title}
                compact
                showResourceLinks
              />
              <p className="text-xs text-slate-600">Use the official learning link above. If a course gives you a certificate, upload it through My training; your manager still checks the evidence and any practical skills.</p>
              {hasLearningContent ? (
                <>
                  {learningContent.overview && (
                    <div className="rounded-lg bg-[#D9F1F4]/30 border border-[#D9F1F4] p-4">
                      <p className="text-sm font-semibold text-[#0F5C5E] mb-1">Before you answer</p>
                      <p className="text-sm text-[#17324D]">{learningContent.overview}</p>
                    </div>
                  )}
                  {learningContent.expected_to_know?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">What you are expected to know or do</h3>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                        {learningContent.expected_to_know.map((item, idx) => <li key={idx}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {learningContent.guidance?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">Practical guidance</h3>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                        {learningContent.guidance.map((item, idx) => <li key={idx}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {(learningContent.dos?.length > 0 || learningContent.donts?.length > 0) && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {learningContent.dos?.length > 0 && (
                        <div className="rounded-lg border border-green-100 bg-green-50 p-3">
                          <h3 className="text-sm font-semibold text-green-800 mb-2">Do</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-green-800">
                            {learningContent.dos.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                      {learningContent.donts?.length > 0 && (
                        <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                          <h3 className="text-sm font-semibold text-red-800 mb-2">Do not</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-red-800">
                            {learningContent.donts.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <p className="text-sm text-slate-700">Read this standard carefully, then continue to the reflective questions.</p>
                </div>
              )}
            </div>
          )}

          {!loading && step === 'form' && schema && schema.fields && schema.fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-slate-900">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.hint && (
                <p className="text-xs text-slate-500 mt-0.5">{field.hint}</p>
              )}
              <FormField
                field={field}
                value={formData[field.key]}
                onChange={handleChange}
                disabled={isReadOnly}
              />
              {field.helper_text && (
                <p className="text-xs text-slate-400 mt-1.5 italic">{field.helper_text}</p>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        {!loading && step === 'learning' && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            {isReadOnly ? (
              <>
                <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50">
                  Close
                </button>
                <button
                  onClick={() => setStep('form')}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
                >
                  View your answers
                </button>
              </>
            ) : (
              <button
                onClick={() => setStep('form')}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#0F5C5E] hover:bg-[#0c4a4c]"
              >
                Continue to questions
              </button>
            )}
          </div>
        )}
        {!loading && step === 'form' && !isReadOnly && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            <button
              onClick={() => setStep('learning')}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
            >
              Back to guidance
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#0F5C5E] hover:bg-[#0c4a4c] disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Submit for review'}
            </button>
          </div>
        )}
        {!loading && step === 'form' && isReadOnly && (
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            <button onClick={() => setStep('learning')} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50">
              Back to guidance
            </button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

export default function CareCertificateInductionPanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null); // {form_id, standard_number, title}
  const [showFullCare, setShowFullCare] = useState(false);
  const [uploadingPreviousCertificate, setUploadingPreviousCertificate] = useState(false);
  const previousCertificateInputRef = useRef(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('workerToken');
    try {
      const response = await axios.get(
        `${API}/worker/induction/overview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOverview(response.data);
    } catch {
      toast.error('Could not load induction status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleFormSaved = () => fetchOverview();
  const handleFormSubmitted = () => {
    setActiveForm(null);
    fetchOverview();
  };

  const handlePreviousCertificateUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploadingPreviousCertificate) return;

    setUploadingPreviousCertificate(true);
    try {
      const token = localStorage.getItem('workerToken');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API}/worker/upload-document/care_certificate`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      toast.success('Care Certificate uploaded for Osabea review');
      await fetchOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not upload your Care Certificate.');
    } finally {
      setUploadingPreviousCertificate(false);
      if (previousCertificateInputRef.current) previousCertificateInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <WorkerPremiumCard accent="purple" className="p-8 text-center text-sm text-slate-500">
        Loading your induction…
      </WorkerPremiumCard>
    );
  }

  if (!overview) return null;

  const {
    items = [],
    overall_status,
    completed: mandatory_completed,
    total: mandatory_total,
  } = overview;

  const pct = mandatory_total > 0 ? Math.round((mandatory_completed / mandatory_total) * 100) : 0;
  const pathway = overview?.care_certificate_pathway || {};
  const previousCertificateEvidence = overview?.previous_care_certificate_evidence || null;
  const previousCertificateAwaitingReview = Boolean(
    previousCertificateEvidence && !previousCertificateEvidence.rejected && !pathway.previous_certificate_recognised
  );
  const nextWorkerItem = items.find(itemNeedsWorkerAction);
  const firstIncompleteItem = items.find((item) => item.status !== 'completed');
  const nextItem = nextWorkerItem || firstIncompleteItem;
  const waitingOnOsabea = Boolean(nextItem && !itemNeedsWorkerAction(nextItem));
  const awaitingReviewCount = items.filter(
    (i) => i.worker_action === 'awaiting_review' || i.submission_status === 'submitted'
  ).length;

  return (
    <div className="space-y-4">
      <TrainingAuditHub mode="worker" />
      <WorkerPremiumCard id="care-certificate-tasks" accent="purple">
        <WorkerPremiumCardHeader
          icon={GraduationCap}
          title="Care Certificate Progress"
          description={`${mandatory_completed} of ${mandatory_total} official standards complete`}
          action={<StatusBadge status={overall_status} />}
        />
        {overall_status === 'completed' && (
          <p className="mt-2 rounded-lg bg-teal-50 p-3 text-sm text-teal-900"
            data-testid="induction-completed-renewal-info">
            Induction achieved. If a related training certificate expires, renew that course in
            My training. You do not need to repeat the Care Certificate.
          </p>
        )}
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-[#0F5C5E] h-2.5 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-[#17324D] shrink-0">{pct}%</span>
        </div>
        {pathway.previous_certificate_recognised ? (
          <p className="mt-2 text-xs text-slate-500">
            Previous Care Certificate recognised for {pathway.recognised_standard_count} standard{pathway.recognised_standard_count === 1 ? '' : 's'}. Only outstanding requirements are shown.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              {previousCertificateAwaitingReview
                ? 'Previous Care Certificate received — Osabea review is pending.'
                : previousCertificateEvidence?.rejected
                  ? 'Your previous Care Certificate needs a replacement.'
                  : 'Already have a Care Certificate? You can send it to Osabea for review.'}
            </span>
            {!previousCertificateAwaitingReview && (
              <>
                <input
                  ref={previousCertificateInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handlePreviousCertificateUpload}
                  data-testid="previous-care-certificate-input"
                />
                <button
                  type="button"
                  onClick={() => previousCertificateInputRef.current?.click()}
                  disabled={uploadingPreviousCertificate}
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:opacity-60"
                >
                  {uploadingPreviousCertificate
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Upload className="h-3 w-3" />}
                  {previousCertificateEvidence?.rejected ? 'Upload replacement' : 'Upload certificate'}
                </button>
              </>
            )}
          </div>
        )}
      </WorkerPremiumCard>

      <WorkerPremiumCard accent="teal" className="border-primary/20 bg-gradient-to-br from-primary/[0.06] to-white">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary/70 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              {nextItem ? (waitingOnOsabea ? 'Osabea action' : 'Your next step') : 'Complete'}
            </p>
            <p className="mt-1 font-heading text-base font-bold text-[#17324D]">
              {nextItem ? `${nextItem.standard_number}. ${itemLabel(nextItem)}` : 'All official standards complete'}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {nextItem
                ? nextItem.worker_action === 'training_required' && nextItem.standard_number === 16
                  ? 'Your organisation will arrange the correct learning and any required Tier 2 practical session.'
                  : waitingOnOsabea
                    ? 'Nothing to chase — Osabea owns the next action and will update CareOps.'
                    : 'One clear step at a time — CareOps will guide you.'
                : awaitingReviewCount > 0
                  ? `${awaitingReviewCount} standard${awaitingReviewCount !== 1 ? 's' : ''} with your manager — nothing else needed from you right now`
                  : 'Great work — you have finished the official Care Certificate standards'}
            </p>
          </div>
        </div>

        {nextItem ? (() => {
          const displayStatus = nextItem.completion_type === 'hybrid' ? getHybridDisplayStatus(nextItem) : (nextItem.status || 'pending');
          return (
            <div className="rounded-xl border border-primary/15 bg-white/80 p-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <StatusBadge status={nextItem.worker_action === 'training_required' ? 'training_required' : displayStatus} />
                {renderHybridActionButton(nextItem, () => setActiveForm(nextItem)) || (
                  waitingOnOsabea
                    ? <span className="text-xs font-medium text-slate-500">No action needed from you</span>
                    : null
                )}
              </div>

            </div>
          );
        })() : awaitingReviewCount > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900">
            Your answers are with your manager for review. You will be notified if anything needs changing.
          </div>
        ) : (
          <div className="rounded-xl border border-green-200 bg-green-50/80 px-3 py-2 text-sm font-medium text-green-800">
            All official Care Certificate standards complete — great work!
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowFullCare(true)}
          className="mt-3 w-full rounded-lg py-2 text-center text-xs font-medium text-primary hover:bg-primary/5"
        >
          View full Care Certificate ({mandatory_total} standards) →
        </button>
      </WorkerPremiumCard>

      {/* Modal for full list - keeps the detailed table available without cluttering the main view */}
      {showFullCare && (
        <div className="fixed inset-0 z-[10050] flex items-end md:items-center justify-center bg-black/40" onClick={() => setShowFullCare(false)}>
          <div role="dialog" aria-modal="true" aria-label="Full Care Certificate Progress"
            className="bg-white w-full md:w-[760px] rounded-t-2xl md:rounded-2xl p-4 max-h-[85vh] overflow-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Full Care Certificate Progress</div>
              <button onClick={() => setShowFullCare(false)} className="text-xl leading-none">×</button>
            </div>
            {/* Compact overview of the 16 standards; only actionable cards
                expose a form. Historical achievements never get reset here. */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3"
              data-testid="care-certificate-modal-grid">
              {items.map(item => {
                const displayStatus = item.completion_type === 'hybrid'
                  ? getHybridDisplayStatus(item)
                  : (item.status || 'pending');
                const openForm = () => { setShowFullCare(false); setActiveForm(item); };
                const actionBtn = item.completion_type === 'hybrid'
                  ? renderHybridActionButton(item, openForm, { size: 'xs' })
                  : null;
                return (
                  <div key={item.code} className="flex min-h-32 flex-col justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3"
                    data-testid={`care-standard-${item.code}`}>
                    <div>
                      <span className="text-xs text-slate-500">Standard {item.standard_number}</span>
                      <p className="mt-1 text-sm font-semibold leading-snug text-slate-900">{itemLabel(item)}</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <StatusBadge status={displayStatus} />
                      {actionBtn}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-xs text-slate-500">Complete the hybrid/self-assessment ones in order. Automatic ones are covered by your training uploads.</div>
          </div>
        </div>
      )}

      {/* Hybrid form modal */}
      {activeForm && (
        <HybridFormModal
          formEntry={activeForm}
          onClose={() => setActiveForm(null)}
          onSaved={handleFormSaved}
          onSubmitted={handleFormSubmitted}
        />
      )}
    </div>
  );
}

